import { useEffect, useState } from "react";
import http from "../../utils/http";
import { Box, CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { VisitPhaseRoot } from "../../types/VisitPhases.types";
import { PhasesList } from "../Responses/types";
import { handlePhaseStatus } from "../Responses/utils";
import { mapScreeningPhaseFormToCard } from "./screeningFormUtils";
import { getFormData } from "../../Redux/actions/responseAction";
import {
  reset,
  setIsParticipant,
  setParticipantLang,
  setPhaseList,
  setResponseIsEditable,
  setResponseLoader,
  setScreeningAttemptId,
  setIsAdminAttempted,
} from "../../Redux/reducers/responseSlice";
import ScreeningAttempt from "./ScreeningAttemp";
import { useNavigate, useParams } from "react-router-dom";
import ScreeningLoadError from "./ScreeningLoadError";
import ScreeningNotConfigured from "./ScreeningNotConfigured";

type ScreeningView = "loading" | "ready" | "error" | "not_configured";

const Screening = () => {
  const [view, setView] = useState<ScreeningView>("loading");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const backgroundColor = useAppSelector(
    (state) => state.study.backgroundColor,
  );
  const [languages, setLanguages] = useState<any[]>([]);
  const [toggle, setToggle] = useState(false);
  const { id: studyId } = useParams();

  useEffect(() => {
    async function getSurveyData() {
      try {
        setView("loading");
        dispatch(setResponseLoader(true));
        const studySlug = localStorage.getItem("study-slug") || "";
        const langRes = await http.get(
          `/data/study-languages?studySlug=${studySlug}`,
        );

        const studyLangs = langRes?.data?.data || [];

        const lang = localStorage.getItem("lang") || "en";

        if (lang !== "en") {
          dispatch(setParticipantLang(lang));
        }
        setLanguages(studyLangs);

        const attemptRes = await http.post(
          `/study/${studyId}/participant-screening-responses/attempt`,
        );

        const screeningAttemptId = attemptRes.data?.data?.attemptId;

        dispatch(
          setScreeningAttemptId({
            screeningAttemptId,
          }),
        );
        dispatch(
          setIsAdminAttempted(
            attemptRes.data?.data?.hasAdminAttempted || false,
          ),
        );

        const { data } = await http.get<VisitPhaseRoot>(
          `/participant/study/${studyId}/study-phase/participant/screening`,
        );

        const screeningPhase = data?.data?.[0];

        if (!screeningPhase?.id) {
          dispatch(setResponseLoader(false));
          setView("not_configured");
          return;
        }

        if (attemptRes.data?.data?.isReadOnly) {
          navigate("/participant/thank-you?type=screening", { replace: true });
          return;
        }

        dispatch(setIsParticipant(true));
        dispatch(setResponseIsEditable(!attemptRes.data?.data?.isReadOnly));

        const phases: PhasesList[] = (data?.data ?? []).map((phase: any) => {
          const phaseForms = (phase.phaseForms ?? []).map((phaseForm: any) =>
            mapScreeningPhaseFormToCard(phaseForm, phase, screeningAttemptId),
          );
          return {
            id: phase.id,
            name: phase.name,
            position: phase.position,
            phaseStatus: handlePhaseStatus(phaseForms),
            phaseForms,
          };
        });

        const currentForm = phases?.[0]?.phaseForms?.[0] ?? null;

        if (!currentForm) {
          dispatch(setResponseLoader(false));
          setView("not_configured");
          return;
        }

        sessionStorage.setItem("response-visit-form", currentForm.id);
        dispatch(getFormData({ studyId, selectedForm: currentForm }));
        dispatch(setPhaseList({ list: phases }));
        setView("ready");
      } catch {
        dispatch(setResponseLoader(false));
        setView("error");
      }
    }

    if (studyId) {
      getSurveyData();
    }

    return () => {
      dispatch(reset());
    };
  }, [studyId, dispatch, toggle, navigate]);

  const refreshPage = () => {
    setToggle((prev: boolean) => !prev);
  };

  const handleRetry = () => {
    setToggle((prev: boolean) => !prev);
  };

  if (view === "loading") {
    return (
      <Box
        sx={{
          backgroundColor: backgroundColor,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (view === "error") {
    return <ScreeningLoadError onRetry={handleRetry} />;
  }

  if (view === "not_configured") {
    return <ScreeningNotConfigured />;
  }

  return (
    <Box sx={{ backgroundColor: backgroundColor }}>
      <ScreeningAttempt languages={languages} refreshPage={refreshPage} />
    </Box>
  );
};

export default Screening;
